import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Box, Typography, IconButton, Menu, MenuItem, TextField, InputAdornment, Divider, Button } from "@material-ui/core";
import { MoreVert, Edit, Block, CheckCircle, TrendingUp, Search, Receipt, Delete, CheckCircleOutline, Add } from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import CompanyModal from "../../components/CompaniesModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import moment from "moment";
import ColorModeContext from "../../layout/themeContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_COMPANIES") {
    const companies = action.payload;
    const newCompanies = [];
    companies.forEach((c) => {
      const idx = state.findIndex((u) => u.id === c.id);
      if (idx !== -1) { state[idx] = c; } else { newCompanies.push(c); }
    });
    return [...state, ...newCompanies];
  }
  if (action.type === "UPDATE_COMPANIES") {
    const c = action.payload;
    const idx = state.findIndex((u) => u.id === c.id);
    if (idx !== -1) { state[idx] = c; return [...state]; }
    return [c, ...state];
  }
  if (action.type === "DELETE_COMPANIES") {
    const idx = state.findIndex((u) => u.id === action.payload);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
  return state;
};

const useStyles = makeStyles((theme) => ({
  container: { padding: "24px", background: theme.palette.background.default, minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "16px" },
  title: { fontSize: "24px", fontWeight: 700, color: theme.palette.text.primary, margin: 0 },
  filterRow: { display: "flex", gap: "12px", marginBottom: "20px", alignItems: "center", flexWrap: "wrap" },
  tableCard: { background: theme.palette.background.paper, borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: `1px solid ${theme.palette.divider}`, overflow: "hidden" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: theme.palette.text.secondary, textTransform: "uppercase", letterSpacing: "0.5px", background: theme.palette.background.default, borderBottom: `2px solid ${theme.palette.divider}`, position: "sticky", top: 0 },
  td: { padding: "12px 16px", fontSize: 13, color: theme.palette.text.primary, borderBottom: `1px solid ${theme.palette.divider}` },
  badge: { padding: "3px 10px", borderRadius: 4, fontSize: 12, fontWeight: 600, display: "inline-block" },
  actions: { display: "flex", gap: "4px", alignItems: "center" },
  emptyState: { textAlign: "center", padding: "48px 20px", color: theme.palette.text.secondary },
  // Modal faturas
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center" },
  modalBox: { background: theme.palette.background.paper, borderRadius: 12, padding: 24, width: "95%", maxWidth: 700, maxHeight: "85vh", overflowY: "auto", overflowX: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${theme.palette.divider}` },
  inlineInput: { padding: "6px 8px", border: `1px solid ${theme.palette.divider}`, borderRadius: 4, fontSize: 13, fontFamily: "inherit", background: theme.palette.background.default, color: theme.palette.text.primary, outline: "none", width: "100%", boxSizing: "border-box" },
  btn: { padding: "8px 16px", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 },
  confirmBox: { background: theme.palette.background.paper, borderRadius: 12, padding: 32, maxWidth: 400, width: "90%", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" },
}));

const statusInvoice = (s) => {
  if (s === "paid") return { label: "Paga", bg: "#d1fae5", color: "#065f46" };
  if (s === "canceled") return { label: "Cancelada", bg: "#fee2e2", color: "#991b1b" };
  return { label: "Aberta", bg: "#fef3c7", color: "#92400e" };
};

const Companies = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const { user, socket } = useContext(AuthContext);
  const { mode } = useContext(ColorModeContext);
  const { dateToClient } = useDate();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [companies, dispatch] = useReducer(reducer, []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCompanyForAction, setSelectedCompanyForAction] = useState(null);

  // Faturas
  const [invoicesModalOpen, setInvoicesModalOpen] = useState(false);
  const [invoicesCompany, setInvoicesCompany] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({});
  const [confirmDeleteInvoice, setConfirmDeleteInvoice] = useState(null);

  useEffect(() => {
    if (!user.super) {
      toast.error("Sem permissão.");
      setTimeout(() => history.push("/"), 1000);
    }
  }, []);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/companiesPlan/", { params: { searchParam, pageNumber } });
        dispatch({ type: "LOAD_COMPANIES", payload: data.companies });
        setHasMore(data.hasMore);
      } catch (err) { toastError(err); }
      setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber]);

  const handleCloseCompanyModal = () => {
    setSelectedCompany(null);
    setCompanyModalOpen(false);
    dispatch({ type: "RESET" });
    setPageNumber(1);
    api.get("/companiesPlan/", { params: { searchParam, pageNumber: 1 } })
      .then(({ data }) => dispatch({ type: "LOAD_COMPANIES", payload: data.companies }))
      .catch(toastError);
  };

  const handleToggleStatus = async (company) => {
    if (!company) return;
    try {
      const newStatus = !(company.status === false ? false : true);
      await api.put(`/companies/${company.id}`, { status: newStatus });
      dispatch({ type: "UPDATE_COMPANIES", payload: { ...company, status: newStatus } });
      toast.success(newStatus ? "Empresa ativada!" : "Empresa desativada!");
    } catch (err) { toastError(err); }
    handleMenuClose();
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await api.delete(`/companies/${companyId}`);
      dispatch({ type: "DELETE_COMPANIES", payload: companyId });
      toast.success(i18n.t("compaies.toasts.deleted"));
    } catch (err) { toastError(err); }
    setDeletingCompany(null);
    setConfirmModalOpen(false);
  };

  const handleMenuOpen = (e, company) => { setAnchorEl(e.currentTarget); setSelectedCompanyForAction(company); };
  const handleMenuClose = () => { setAnchorEl(null); setSelectedCompanyForAction(null); };

  const handleOpenInvoices = async (company) => {
    setInvoicesCompany(company);
    setInvoicesModalOpen(true);
    setInvoicesLoading(true);
    handleMenuClose();
    try {
      const { data } = await api.get("/invoices/all", { params: { companyId: company.id, pageNumber: 1 } });
      setInvoices(data.invoices || []);
    } catch (err) { toastError(err); }
    setInvoicesLoading(false);
  };

  const handleMarkAsPaid = async (invoice) => {
    try {
      await api.put(`/invoices/${invoice.id}`, { status: "paid" });
      setInvoices(prev => prev.map(i => i.id === invoice.id ? { ...i, status: "paid" } : i));

      // Renovar dueDate da empresa baseado na recorrência
      const company = invoicesCompany;
      if (company) {
        const recurrence = company.recurrence || "monthly";
        const currentDue = company.dueDate ? moment(company.dueDate) : moment();
        const base = currentDue.isAfter(moment()) ? currentDue : moment();
        let newDueDate;
        if (recurrence === "yearly" || recurrence === "anual") {
          newDueDate = base.clone().add(1, "year").format("YYYY-MM-DD");
        } else {
          newDueDate = base.clone().add(1, "month").format("YYYY-MM-DD");
        }
        await api.put(`/companies/${company.id}`, { dueDate: newDueDate, status: true });
        toast.success(`Fatura paga! Plano renovado até ${moment(newDueDate).format("DD/MM/YYYY")}`);
      } else {
        toast.success("Fatura marcada como paga!");
      }
    } catch (err) { toastError(err); }
  };

  const handleDeleteInvoice = async () => {
    try {
      await api.delete(`/invoices/${confirmDeleteInvoice.id}`);
      setInvoices(prev => prev.filter(i => i.id !== confirmDeleteInvoice.id));
      setConfirmDeleteInvoice(null);
      toast.success("Fatura excluída!");
    } catch (err) { toastError(err); }
  };

  const handleSaveInvoice = async () => {
    try {
      await api.put(`/invoices/${editingInvoice.id}`, invoiceForm);
      setInvoices(prev => prev.map(i => i.id === editingInvoice.id ? { ...i, ...invoiceForm } : i));
      setEditingInvoice(null);
      setInvoiceForm({});
      toast.success("Fatura atualizada!");
    } catch (err) { toastError(err); }
  };

  const renderPlanValue = (row) => row.planId !== null && row.plan?.amount
    ? row.plan.amount.toLocaleString("pt-br", { minimumFractionDigits: 2 }) : "-";

  const getDueDateStyle = (dueDate) => {
    if (!moment(dueDate).isValid()) return {};
    const diff = moment(dueDate).diff(moment(), "days");
    if (diff <= 0) return { color: "#ef4444", fontWeight: 600 };
    if (diff <= 5) return { color: "#f59e0b", fontWeight: 600 };
    return {};
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber(p => p + 1);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingCompany && `${i18n.t("compaies.confirmationModal.deleteTitle")} ${deletingCompany.name}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCompany(deletingCompany.id)}
      >
        {i18n.t("compaies.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <CompanyModal
        open={companyModalOpen}
        onClose={handleCloseCompanyModal}
        companyId={selectedCompany?.id}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleOpenInvoices(selectedCompanyForAction)}>
          <Receipt style={{ marginRight: 8 }} fontSize="small" /> Ver Faturas
        </MenuItem>
      </Menu>

      <div className={classes.container}>
        <div className={classes.header}>
          <h1 className={classes.title}><Receipt style={{ verticalAlign: "middle", marginRight: 8 }} />Faturas ({companies.length})</h1>
        </div>

        <div className={classes.filterRow}>
          <TextField
            variant="outlined" size="small" placeholder="Buscar empresas..."
            value={searchParam} onChange={e => setSearchParam(e.target.value.toLowerCase())}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            style={{ flex: 1, maxWidth: 400 }}
          />
        </div>

        <div className={classes.tableCard}>
          <div style={{ overflowX: "auto", maxHeight: "calc(100vh - 260px)", overflowY: "auto" }} onScroll={handleScroll}>
            <table className={classes.table}>
              <thead>
                <tr>
                  {["ID", "Nome", "Email", "Plano", "Valor", "Vencimento", "Status", "Ações"].map(h => (
                    <th key={h} className={classes.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && companies.length === 0 ? (
                  <tr><td colSpan={8} className={classes.emptyState}>Carregando...</td></tr>
                ) : companies.length === 0 ? (
                  <tr><td colSpan={8} className={classes.emptyState}>Nenhuma empresa encontrada</td></tr>
                ) : companies.map(company => {
                  const isActive = company.status !== false;
                  return (
                    <tr key={company.id} style={{ transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = theme.palette.action.hover}
                      onMouseLeave={e => e.currentTarget.style.background = ""}>
                      <td className={classes.td} style={{ color: theme.palette.text.secondary }}>#{company.id}</td>
                      <td className={classes.td} style={{ fontWeight: 600 }}>{company.name}</td>
                      <td className={classes.td}>{company.email || "-"}</td>
                      <td className={classes.td}>{company?.plan?.name || "-"}</td>
                      <td className={classes.td}>R$ {renderPlanValue(company)}</td>
                      <td className={classes.td} style={getDueDateStyle(company.dueDate)}>{dateToClient(company.dueDate)}</td>
                      <td className={classes.td}>
                        <span className={classes.badge} style={{ background: isActive ? "#d1fae5" : "#fee2e2", color: isActive ? "#065f46" : "#991b1b" }}>
                          {isActive ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className={classes.td}>
                        <div className={classes.actions}>
                          <IconButton size="small" title="Ver Faturas" onClick={() => handleOpenInvoices(company)}>
                            <Receipt fontSize="small" style={{ color: "#f59e0b" }} />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Faturas */}
      {invoicesModalOpen && (
        <div className={classes.overlay}>
          <div className={classes.modalBox}>
            <div className={classes.modalHeader}>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                <Receipt style={{ verticalAlign: "middle", marginRight: 8 }} />
                Faturas — {invoicesCompany?.name}
              </Typography>
              <IconButton size="small" onClick={() => { setInvoicesModalOpen(false); setEditingInvoice(null); }}>✕</IconButton>
            </div>

            {invoicesLoading ? (
              <Typography style={{ textAlign: "center", padding: 32 }}>Carregando...</Typography>
            ) : invoices.length === 0 ? (
              <Typography style={{ textAlign: "center", padding: 32, color: "#999" }}>Nenhuma fatura encontrada</Typography>
            ) : (
              <table className={classes.table} style={{ tableLayout: "fixed" }}>
                <thead>
                  <tr>
                    <th className={classes.th} style={{ width: "28%" }}>Detalhe</th>
                    <th className={classes.th} style={{ width: "18%" }}>Vencimento</th>
                    <th className={classes.th} style={{ width: "16%" }}>Valor</th>
                    <th className={classes.th} style={{ width: "16%" }}>Status</th>
                    <th className={classes.th} style={{ width: "22%" }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(invoice => {
                    const st = statusInvoice(invoice.status);
                    const isEditing = editingInvoice?.id === invoice.id;
                    return (
                      <tr key={invoice.id}>
                        <td className={classes.td}>
                          {isEditing
                            ? <input className={classes.inlineInput} value={invoiceForm.detail || ""} onChange={e => { const v = e.target.value; setInvoiceForm(p => ({ ...p, detail: v })); }} />
                            : invoice.detail}
                        </td>
                        <td className={classes.td}>
                          {isEditing
                            ? <input type="date" className={classes.inlineInput} value={invoiceForm.dueDate || ""} onChange={e => { const v = e.target.value; setInvoiceForm(p => ({ ...p, dueDate: v })); }} />
                            : moment(invoice.dueDate).format("DD/MM/YYYY")}
                        </td>
                        <td className={classes.td}>
                          {isEditing
                            ? <input type="number" className={classes.inlineInput} style={{ width: 90 }} value={invoiceForm.value || ""} onChange={e => { const v = e.target.value; setInvoiceForm(p => ({ ...p, value: v })); }} />
                            : Number(invoice.value).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
                        </td>
                        <td className={classes.td}>
                          {isEditing
                            ? <select className={classes.inlineInput} value={invoiceForm.status || ""} onChange={e => { const v = e.target.value; setInvoiceForm(p => ({ ...p, status: v })); }}>
                                <option value="open">Aberta</option>
                                <option value="paid">Paga</option>
                                <option value="canceled">Cancelada</option>
                              </select>
                            : <span className={classes.badge} style={{ background: st.bg, color: st.color }}>{st.label}</span>}
                        </td>
                        <td className={classes.td}>
                          <div className={classes.actions}>
                            {isEditing ? (
                              <>
                                <button className={classes.btn} style={{ background: "#3b82f6", color: "white" }} onClick={handleSaveInvoice}>Salvar</button>
                                <button className={classes.btn} style={{ background: "#e8ecf1", color: "#1a1a1a" }} onClick={() => setEditingInvoice(null)}>Cancelar</button>
                              </>
                            ) : (
                              <>
                                <IconButton size="small" title="Editar" onClick={() => { setEditingInvoice(invoice); setInvoiceForm({ detail: invoice.detail, dueDate: moment(invoice.dueDate).format("YYYY-MM-DD"), value: invoice.value, status: invoice.status }); }}>
                                  <Edit fontSize="small" style={{ color: "#3b82f6" }} />
                                </IconButton>
                                {invoice.status !== "paid" && (
                                  <IconButton size="small" title="Marcar como paga" onClick={() => handleMarkAsPaid(invoice)}>
                                    <CheckCircleOutline fontSize="small" style={{ color: "#10b981" }} />
                                  </IconButton>
                                )}
                                <IconButton size="small" title="Excluir" onClick={() => setConfirmDeleteInvoice(invoice)}>
                                  <Delete fontSize="small" style={{ color: "#ef4444" }} />
                                </IconButton>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Confirmar exclusão fatura */}
      {confirmDeleteInvoice && (
        <div className={classes.overlay} style={{ zIndex: 1400 }}>
          <div className={classes.confirmBox}>
            <Typography variant="h6" style={{ marginBottom: 12, fontWeight: 700 }}>Confirmar exclusão</Typography>
            <Typography style={{ marginBottom: 24, color: "#666" }}>
              Deseja excluir a fatura de <strong>{moment(confirmDeleteInvoice.dueDate).format("DD/MM/YYYY")}</strong>?
            </Typography>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button className={classes.btn} style={{ background: "#e8ecf1", color: "#1a1a1a" }} onClick={() => setConfirmDeleteInvoice(null)}>Cancelar</button>
              <button className={classes.btn} style={{ background: "#ef4444", color: "white" }} onClick={handleDeleteInvoice}>Excluir</button>
            </div>
          </div>
        </div>
      )}
    </MainContainer>
  );
};

export default Companies;
